import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Generate a cryptographically secure session ID
 * Uses Web Crypto API instead of Math.random()
 */
export function generateSecureId(prefix: string = 'id'): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  const hex = Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  return `${prefix}_${hex}`;
}

/**
 * Format phone number as user types (US format)
 * @param value - Raw input string
 * @returns Formatted phone number string
 */
export function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
}

/**
 * Validate and sanitize a URL slug
 * @param name - Input string to convert to slug
 * @returns Sanitized slug or null if invalid
 */
export function createSlug(name: string): string | null {
  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  // Validate slug is not empty or too short
  if (!slug || slug.length < 2) {
    return null;
  }

  // Check for reserved words
  const reserved = ['admin', 'api', 'widget', 'dashboard', 'settings', 'onboarding'];
  if (reserved.includes(slug)) {
    return `${slug}-company`;
  }

  return slug;
}

/**
 * Validate phone number has exactly 10 digits
 * @param phone - Phone number string (formatted or raw)
 * @returns true if valid
 */
export function isValidPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10;
}

// ============================================
// LOCAL STORAGE ENCRYPTION (for PII protection)
// Uses Web Crypto API with AES-GCM
// ============================================

const ENCRYPTION_KEY_NAME = 'renovation_widget_key';

/**
 * Get or generate an encryption key for this browser session
 * Key is stored in sessionStorage (cleared when browser closes)
 */
async function getEncryptionKey(): Promise<CryptoKey> {
  // Check if we have a key in sessionStorage
  const storedKey = sessionStorage.getItem(ENCRYPTION_KEY_NAME);

  if (storedKey) {
    const keyData = JSON.parse(storedKey);
    return await crypto.subtle.importKey(
      'jwk',
      keyData,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
  }

  // Generate a new key
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );

  // Export and store the key
  const exportedKey = await crypto.subtle.exportKey('jwk', key);
  sessionStorage.setItem(ENCRYPTION_KEY_NAME, JSON.stringify(exportedKey));

  return key;
}

/**
 * Encrypt data before storing in localStorage
 * @param data - Object to encrypt
 * @returns Base64 encoded encrypted string
 */
export async function encryptForStorage<T>(data: T): Promise<string> {
  const key = await getEncryptionKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoder = new TextEncoder();
  const encoded = encoder.encode(JSON.stringify(data));

  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoded
  );

  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);

  // Convert to base64
  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypt data from localStorage
 * @param encrypted - Base64 encoded encrypted string
 * @returns Decrypted object or null if decryption fails
 */
export async function decryptFromStorage<T>(encrypted: string): Promise<T | null> {
  try {
    const key = await getEncryptionKey();

    // Decode from base64
    const combined = new Uint8Array(
      atob(encrypted).split('').map(c => c.charCodeAt(0))
    );

    // Extract IV and encrypted data
    const iv = combined.slice(0, 12);
    const data = combined.slice(12);

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );

    const decoder = new TextDecoder();
    return JSON.parse(decoder.decode(decrypted)) as T;
  } catch {
    // Decryption failed - key may have changed (new session)
    return null;
  }
}
